import { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@orbita-ui/core';

const StyledCurrencyInputContainer = styled.div<{ $invalid: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  div.label {
    display: flex;
    gap: 4px;

    label {
      color: rgb(62, 76, 89);
      font-family: Inter, sans-serif;
      font-size: 0.75rem;
      letter-spacing: 0em;
      line-height: 1rem;
      font-weight: 400;
    }
  }

  div.input-container {
    width: 100%;
    display: flex;
    align-items: flex-start;
    vertical-align: middle;
    height: 40px;
    gap: 10px;
    box-sizing: border-box;
    background: rgb(255, 255, 255);
    border-radius: 48px;
    padding: 7px 11px;
    border: 1px solid;
    border-color: ${(props) =>
      props.$invalid ? 'rgb(225, 45, 57) !important' : 'rgb(203, 210, 217)'};

    div {
      width: 100%;
      display: flex;
      gap: 8px;
    }

    &:hover {
      background: rgb(245, 247, 250);
    }

    &:focus-within {
      border-color: rgb(45, 204, 167);
    }

    span {
      color: rgb(123, 135, 148);
      width: 24px;
      line-height: 1.5rem;
      font-size: 1.5rem;
      text-align: center;
    }

    input {
      width: 100%;
      border: none !important;
      appearance: none;
      outline: none;
      max-height: 24px;
      font-family: Inter, sans-serif;
      font-size: 0.875rem;
      letter-spacing: 0em;
      line-height: 1.5rem;
      font-weight: 400;
      padding: 0px;
      color: rgb(31, 41, 51);
      caret-color: rgb(45, 204, 167);
      background: none !important;
    }
  }

  span.error {
    color: rgb(225, 45, 57);
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    letter-spacing: 0em;
    line-height: 1rem;
    font-weight: 400;

    svg {
      width: 16px;
      height: 16px;
      color: rgb(225, 45, 57);
    }
  }
`;

export type CurrencyInputProps = {
  label?: string;
  placeholder?: string;
  invalid?: boolean;
  errorText?: string;
  value?: any;
  onChange?: any;
  onBlur?: any;
};

export const CurrencyInput = ({
  label,
  placeholder,
  errorText,
  invalid,
  onBlur,
  value: defaultValue,
  onChange,
}: CurrencyInputProps) => {
  const [value, setValue] = useState<number>(isNaN(defaultValue) ? null : defaultValue);

  useEffect(() => {
    setValue(isNaN(defaultValue) ? null : defaultValue);
  }, [defaultValue]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const numberString = e.target.value;
    const numberValue =
      isNaN(Number(numberString)) || Number(numberString) < 0
        ? undefined
        : Number(numberString);
    setValue(numberValue);
    onChange(numberValue);
  };

  return (
    <StyledCurrencyInputContainer $invalid={invalid}>
      {label && (
        <div className="label">
          <label>{label}</label>
        </div>
      )}
      <div className="input-container">
        <div>
          <span>$</span>
          <input
            type="number"
            min="1"
            step="any"
            value={value}
            onChange={handleInputChange}
            onBlur={onBlur}
            placeholder={placeholder}
          />
        </div>
      </div>
      {errorText && (
        <span color="danger" className="error">
          <Icon name="WarningTriangleIcon" /> {errorText}
        </span>
      )}
    </StyledCurrencyInputContainer>
  );
};
